import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { KeycloakService } from 'keycloak-angular';
import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { LicenceTypeId } from '../../entities/payment/payment';
import { Role } from '../../entities/role/role';
import { User, UserActions, UserInput } from '../../entities/user/user';
import { ApolloService } from '../apollo.service';
import * as fromGraphQl from './graphql';

@Injectable()
export class UsersService {
  public theme = 'standard';
  private renderer: Renderer2;

  constructor(
    private apolloService: ApolloService,
    private apollo: Apollo,
    private keycloakAngular: KeycloakService,
    rendererFactory: RendererFactory2,
    private translateService: TranslateService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  public suspendUser(userId: string, callback: (arg: any) => void): any {
    this.apolloService.mutateAndExecute(fromGraphQl.DISABLE_USER_MUTATION, { userId }, callback);
  }

  public enableUser(userId: string, callback: (arg: any) => void): void {
    this.apolloService.mutateAndExecute(fromGraphQl.ENABLE_USER_MUTATION, { userId }, callback);
  }

  public updateRoles(userInput: UserInput, refetchQuery: QueryRef<any>): void {
    this.apolloService.mutate(fromGraphQl.UPDATE_AUTHORITIES_MUTATION, { user: userInput }, refetchQuery);
  }

  public checkEmailNotTaken(email: string): Observable<boolean> {
    return this.apollo
      .mutate({ mutation: fromGraphQl.CHECK_EMAIL_MUTATION, variables: { email } })
      .pipe(map((result: any) => result.data.checkEmailAddress));
  }

  public updateUser(userInput: UserInput, callback: (arg: any) => void): void {
    this.apolloService.mutateAndExecute(fromGraphQl.UPDATE_USER_MUTATION, { input: userInput }, callback);
  }

  public createUser(userInput: UserInput, callback: any): void {
    this.apolloService.mutateAndExecute(fromGraphQl.CREATE_USER_MUTATION, { input: userInput }, callback);
  }

  public resetPassword(id: string, password: string, requiredActions: string[], callback: (arg: any) => void): void {
    this.apolloService.mutateAndExecute(fromGraphQl.RESET_PASSWORD_MUTATION, { id, password, requiredActions }, callback);
  }

  public changeSite(userInput: UserInput, refetchQuery: QueryRef<any>): void {
    this.apolloService.mutate(fromGraphQl.CHANGE_SITE_MUTATION, { user: userInput }, refetchQuery);
  }

  public getCurrentTenantId(): string | undefined {
    const key = 'tenant_id';
    if (this.keycloakAngular.getKeycloakInstance()?.tokenParsed) {
      return (this.keycloakAngular.getKeycloakInstance().tokenParsed as any)[key];
    }
    return undefined;
  }

  public getCurrentUserId(): string | undefined {
    const key = 'id';
    if (this.keycloakAngular.getKeycloakInstance()?.tokenParsed) {
      return (this.keycloakAngular.getKeycloakInstance().tokenParsed as any)[key];
    }
    return undefined;
  }

  public getCurrentLicenceType(): string | undefined {
    const key = 'licence_type';
    if (this.keycloakAngular.getKeycloakInstance()?.tokenParsed) {
      const parsedToken = this.keycloakAngular.getKeycloakInstance().tokenParsed as any;
      if (parsedToken['realm_access']?.roles?.includes('framework_licencing_full_user')) {
        return 'FULL_USER';
      }
      if (parsedToken['realm_access']?.roles?.includes('framework_licencing_app_user')) {
        return 'APP_USER';
      }
    }
    return 'NO_LICENCE';
  }

  public isCurrentUserNexatoEmployee(): boolean {
    if (this.keycloakAngular.getKeycloakInstance()?.tokenParsed) {
      const parsedToken = this.keycloakAngular.getKeycloakInstance().tokenParsed as any;
      return parsedToken['realm_access']?.roles?.includes('framework_nexato_employee');
    }
    return false;
  }

  public isNexatoEmployee(user: User): boolean {
    return user.roles.find(role => role.name === 'framework_nexato_employee') !== undefined;
  }

  public switchTenant(tenant: string): void {
    this.apollo
      .mutate({ mutation: fromGraphQl.SWITCH_TENANT_MUTATION, variables: { tenant } })
      .subscribe(
        () => {
          this.keycloakAngular.updateToken(-1).then(() => {
            window.location.reload();
          });
        },
        (error) => {
          console.log('there was an error sending the query', error);
        }
      );
  }

  public setRequiredActions(id: string, requiredActions: UserActions[], callback: (arg: any) => void): void {
    this.apolloService.mutateAndExecute(fromGraphQl.SET_REQUIRED_ACTIONS, { id, requiredActions }, callback);
  }

  public updateLicenceType(userId: string, licenceType: LicenceTypeId, callback: (arg: any) => void): void {
    this.apolloService.mutateAndExecute(fromGraphQl.UPDATE_LICENCE_TYPE, { userId, licenceType }, callback);
  }

  addRoles(user: User, result: Role[], callback: () => void) {
    const input = UserInput.createUserInputFromUser(user);
    const roles = user.roles;
    for (let i = 0; i < result.length; i++) {
      roles.push(result[i]);
    }
    this.apolloService.mutateAndExecute(fromGraphQl.UPDATE_USER_MUTATION, { input }, callback);
  }

  removeRoles(user: User, result: Role[], callback: () => void) {
    const input = UserInput.createUserInputFromUser(user);
    let roles = user.roles;
    for (let i = 0; i < result.length; i++) {
      roles = roles.filter((role) => role.id !== result[i].id);
    }
    this.apolloService.mutateAndExecute(fromGraphQl.UPDATE_USER_MUTATION, { input }, callback);
  }

  /**
   * Speichert die bevorzugte Sprache serverseitig und wendet sie erst NACH der Bestaetigung
   * lokal an (#735). Backend ist die Wahrheit, der localStorage nur Boot-Cache.
   *
   * Bewusst mutateObservable statt mutateAndExecute: letzteres subscribed nur mit `next`, ein
   * GraphQL-Fehler wuerde still verschwinden und der Erfolgs-Toast trotzdem erscheinen.
   * Der Aufrufer subscribed mit { next, error } und zeigt die Toasts.
   */
  public updatePreferredLanguage(language: string): Observable<any> {
    const userId = this.getCurrentUserId();
    return this.apolloService
      .mutateObservable(fromGraphQl.SET_PREFERRED_LANGUAGE_MUTATION, { userId, language })
      .pipe(
        take(1),
        tap(() => {
          // Guard ist Pflicht: getCurrentUserId() ist string | undefined und strictNullChecks
          // ist im Projekt aus — ohne ihn entsteht der Key 'undefined_language'.
          if (!userId) {
            return;
          }
          localStorage.setItem(userId + '_language', language);
          this.translateService.use(language);
        })
      );
  }

  /**
   * Liest den localStorage-Boot-Cache. Seit #735 NICHT mehr die Anzeigequelle im Profil —
   * die kommt aus user.preferredLanguage vom Server. Nur noch Startwert fuer den Sprach-Dialog
   * und Eingabe fuer den einmaligen Abgleich (PreferredLanguageSyncService).
   */
  getCurrentUsersPreferredlanguage(): string | undefined {
    const userId = this.getCurrentUserId();
    if (userId) {
      const language = localStorage.getItem(userId + '_language');
      if (language) {
        return language;
      }
    }
    return undefined;
  }

  public getUsers(): Observable<User[]> {
    return this.apolloService
      .query<fromGraphQl.GetUsersResponse>(fromGraphQl.GET_USERS_QUERY)
      .pipe(
        take(1),
        switchMap((result) => of(User.createUsers(result.data.getUsers)))
      );
  }

  public getActiveUsers(): Observable<User[]> {
    return this.apolloService
      .query<fromGraphQl.GetEnabledUsersResponse>(fromGraphQl.GET_ENABLED_USERS_QUERY)
      .pipe(
        take(1),
        switchMap((result) => of(User.createUsers(result.data.getEnabledUsers)))
      );
  }
}
